"use client";
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import ReCAPTCHA from "react-google-recaptcha";

import CheckboxWithLink from "@/src/components/emlakjet-link/contact-form-modal/contact-form/checkbox";
import PhoneInput from "@/src/components/common/phone-input";

import { handleFormValidation } from "@/src/helpers/emlakjet-link";
import {
  initialFormErrors,
  initialFormValues,
  stepNames,
  formFieldNames,
  leadStaticData,
} from "@/constants/emlakjet-link";
import { Ga4WithEventParameters } from "@/src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { sendCompanyContactForm } from "@/src/services/emlakjet-link";
import Loading from "@/src/components/emlakjet-link/contact-form-modal/contact-form/loading";
import PPLModal from "src/components/emlakjet-link/ppl-modal";
import { capitalizeFirstWord } from "@/src/helpers/project-detail/logo-placeholder";

import styles from "./styles.module.css";

const ContactForm = ({
  handleStep,
  currentCompany,
  companyLogo,
  icon,
  projectDetailFormData,
  isProjectDetailSticky,
  isProjectRadar,
  project,
  isFirm,
}) => {
  const recaptchaRef = useRef();
  const { company, city, district, name, project: projectForProjectDetail } = currentCompany || {};

  const { SUCCESS, ERROR } = stepNames;
  const { NAME, EMAIL, PHONE, COUNTRY_PHONE_CODE, INDIVIDUAL_KVKK, HAS_ERROR, ERROR_TEXT, STATUS } = formFieldNames;

  const [errors, setErrors] = useState({ ...initialFormErrors });
  const [formValues, setFormValues] = useState({ ...initialFormValues });
  const [isHuman, setIsHuman] = useState(false);
  const [isPPLModalOpen, setIsPPLModalOpen] = useState(false);
  const [enableRecaptcha, setEnableRecaptcha] = useState(false);
  const [loading, setLoading] = useState(false);

  const nameContainerClass = classNames({
    [styles.inputContainer]: true,
    [styles.error]: errors[NAME][STATUS],
  });

  const emailContainerClass = classNames({
    [styles.inputContainer]: true,
    [styles.error]: errors[EMAIL][STATUS],
  });

  const contactFormWrapperClass = classNames({
    [styles.contactFormWrapper]: !isProjectDetailSticky,
    [styles.contactFormWrapperForCompanyDetail]: companyLogo,
    [styles.contactFormWrapperForProjectDetailSticky]: isProjectDetailSticky,
  });

  const handleFormValues = (key, value) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCheckboxClick = () => setFormValues((prev) => ({ ...prev, individualKVKK: !prev.individualKVKK }));
  const togglePPLModal = () => setIsPPLModalOpen((prev) => !prev);
  const handleCaptcha = () => setIsHuman(true);
  const toggleLoader = () => setLoading((prev) => !prev);

  const validateForm = () => {
    const formErrors = handleFormValidation(formValues);
    if (formErrors[HAS_ERROR]) {
      setErrors(formErrors);
      return false;
    }
    return true;
  };

  const validateAndSubmit = () => {
    if (!validateForm() || loading) return;
    const { name, email } = formValues;
    const phoneNumber = `${formValues[COUNTRY_PHONE_CODE]}${formValues[PHONE].replace(/\s/g, "")}`;
    const companyInfo = { company, city, district };

    const commonFields = {
      ...leadStaticData,
      name,
      phone_number: phoneNumber,
      email,
    };

    const formTypes = {
      radar: {
        type: "radar",
        project: project?.project?.projectId,
        company: project?.company?.companyId,
        city: project?.city?.cityId,
        district: project?.town?.townId,
      },
      project: {
        ...companyInfo,
        type: "project",
        project: projectForProjectDetail,
      },
      firm: {
        city: city?.id,
        district: district?.id,
        company: currentCompany?.id,
      },
    };

    const getFormType = () => {
      if (isProjectRadar) return "radar";
      if (projectDetailFormData || isProjectDetailSticky) return "project";
      if (isFirm) return "firm";
      return null;
    };

    const formType = getFormType();
    if (!formType) return;

    const query = {
      ...commonFields,
      ...formTypes[formType],
    };

    handleSubmit(query);
  };

  const handleSubmit = async (query) => {
    const { phone_number: phoneNumber } = query || {};
    Ga4WithEventParameters({
      eventName: "emlakjet_link_form_gonder",
      eventParameters: { label: name },
    });
    toggleLoader();
    try {
      const res = await sendCompanyContactForm(query);
      if (res.data) {
        handleStep({ step: SUCCESS });
      } else {
        handleStep({ step: ERROR, error: res, phoneNumber });
      }
    } catch (error) {
      handleStep({ step: ERROR, error, phoneNumber });
    } finally {
      toggleLoader();
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!isHuman) {
      recaptchaRef?.current?.execute();
      return;
    }
    validateAndSubmit();
  };

  useEffect(() => {
    if (isHuman) {
      validateAndSubmit();
    }
  }, [isHuman]);

  useEffect(() => {
    if (errors[HAS_ERROR]) {
      const formErrors = handleFormValidation(formValues);
      setErrors(formErrors);
    } else {
      setErrors({ ...initialFormErrors });
    }
  }, [formValues]);

  useEffect(() => {
    if (window?.env) {
      setEnableRecaptcha(true);
    } else {
      setEnableRecaptcha(false);
    }
    setFormValues({ ...initialFormValues });
    setErrors({ ...initialFormErrors });
  }, []);

  const renderFormTitle = () => {
    if (companyLogo || icon) {
      return <p className={styles.formTitle}>Detaylı Bilgi Almak İçin Formu Doldurun, Size Ulaşalım!</p>;
    }
    if (projectDetailFormData) {
      return (
        <p className={styles.projectDetailFormData}>
          <strong className={styles.projectFormDataTitle}>{projectDetailFormData}</strong>
          <strong className={styles.projectFormDataDescription}>
            &nbsp;konut tipleri için formu doldurun sizinle iletişime geçelim!
          </strong>
        </p>
      );
    }
    if (isProjectDetailSticky) {
      return <p className={styles.stickyFormTitle}>Detaylı Bilgi Almak İçin Formu Doldurun, Size Ulaşalım!</p>;
    }
    return null;
  };

  const getCompanyLogo = () => {
    if (!isFirm) return null;
    if (companyLogo) {
      return <img className={styles.logo} src={companyLogo} alt={name} />;
    } else {
      return <div className={styles.logoPlaceholder}>{capitalizeFirstWord(name)}</div>;
    }
  };

  return (
    <>
      {loading && <Loading />}
      <div className={contactFormWrapperClass}>
        {getCompanyLogo()}
        {icon && icon}
        {renderFormTitle()}
        <div className={nameContainerClass}>
          <label className={styles.inputLabel}>Ad Soyad</label>
          <input
            type="text"
            placeholder="Ad Soyad"
            className={styles.inputField}
            onChange={({ target }) => handleFormValues(NAME, target.value)}
          />
          {errors[NAME][STATUS] && <span className={styles.errorText}>{errors[NAME][ERROR_TEXT]}</span>}
        </div>
        <div className={styles.inputContainer}>
          <label className={styles.inputLabel}>Cep Telefonu</label>
          <PhoneInput
            hasError={errors[PHONE][STATUS]}
            phoneNumber={formValues[PHONE]}
            dialCode={formValues[COUNTRY_PHONE_CODE]}
            handleFormValues={handleFormValues}
          />
          {errors[PHONE][STATUS] && <span className={styles.errorText}>{errors[PHONE][ERROR_TEXT]}</span>}
        </div>
        <div className={emailContainerClass}>
          <label className={styles.inputLabel}>E-Posta</label>
          <input
            type="email"
            placeholder="Email Adresi"
            className={styles.inputField}
            onChange={({ target }) => handleFormValues(EMAIL, target.value)}
          />
          {errors[EMAIL][STATUS] && <span className={styles.errorText}>{errors[EMAIL][ERROR_TEXT]}</span>}
        </div>
        <CheckboxWithLink
          hasError={errors[INDIVIDUAL_KVKK][STATUS]}
          isChecked={formValues[INDIVIDUAL_KVKK]}
          onCheckboxChange={handleCheckboxClick}
          onModalOpen={togglePPLModal}
        />
        {enableRecaptcha && (
          <ReCAPTCHA
            key="captcha"
            ref={recaptchaRef}
            sitekey={window.env.EJ_KEYS_RECAPTCHA}
            size="invisible"
            badge="inline"
            onChange={handleCaptcha.bind(this)}
          />
        )}
      </div>
      <button onClick={handleFormSubmit} className={styles.submitButton} type="button">
        Gönder
      </button>
      <PPLModal isOpen={isPPLModalOpen} handleClose={togglePPLModal} />
    </>
  );
};

ContactForm.propTypes = {
  handleStep: PropTypes.func,
  currentCompany: PropTypes.object.isRequired,
  toggleLoader: PropTypes.func,
  loading: PropTypes.bool,
  isProjectRadar: PropTypes.bool,
  project: PropTypes.object,
  companyLogo: PropTypes.string,
  icon: PropTypes.node,
  projectDetailFormData: PropTypes.object,
  isProjectDetailSticky: PropTypes.bool,
  isFirm: PropTypes.bool,
};

export default ContactForm;
